import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { StudioActionError } from '../../studio/StudioActionError';
import { profileEntryMessages as e } from '../entry/profileEntryMessages';
import { headerIsInProfile } from './profileKitRules';
import { useKitProfileShortcut } from './useKitProfileShortcut';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface KitHeaderProfileActionsProps {
  accountId: number;
  catalogItemUuid: string;
  /** The ready kit file of the slot ("Use for my profile" takes its picture). */
  kitAssetUuid: string;
  /** Every ready kit file of the slot: "In your profile" matches any of them. */
  kitAssetUuids: readonly string[];
  canAct: boolean;
}

/**
 * The Profile part of the kit Spotify header slot (design c3, spec D4): "In your profile" when the
 * Profile header came from this release, "Use for my profile" (a new Profile version with the same
 * picture and the look of this release) and "Open Profile". The slot keeps its own actions.
 */
const KitHeaderProfileActionsComponent: React.FC<KitHeaderProfileActionsProps> = (props) => {
  const { accountId, catalogItemUuid, kitAssetUuid, kitAssetUuids, canAct } = props;
  const intl = useIntl();
  const shortcut = useKitProfileShortcut(accountId);
  const inProfile = useMemo(
    () => headerIsInProfile(shortcut.profile?.assets, catalogItemUuid, kitAssetUuids),
    [catalogItemUuid, kitAssetUuids, shortcut.profile]
  );
  const done = shortcut.doneKey === kitAssetUuid;

  return (
    <div className="d-flex flex-column gap-1" data-testid="kit-header-profile">
      {inProfile && (
        <span className={styles.inProfile} data-testid="kit-header-in-profile">
          {intl.formatMessage(e.inYourProfile)}
        </span>
      )}
      {done && <span className={styles.meta}>{intl.formatMessage(e.useForProfileDone)}</span>}
      <div className={styles.row}>
        {canAct && (
          <SMLButton
            className={styles.sm}
            disabled={shortcut.isBusy}
            onClick={() => shortcut.use(catalogItemUuid, kitAssetUuid)}
          >
            {intl.formatMessage(e.useForProfile)}
          </SMLButton>
        )}
        <SMLButton className={styles.sm} onClick={() => shortcut.openProfile(true)}>
          {intl.formatMessage(e.openProfile)}
        </SMLButton>
      </div>
      <StudioActionError feedback={shortcut.feedback} />
    </div>
  );
};

export const KitHeaderProfileActions = TypedMemo(KitHeaderProfileActionsComponent);
